import React from 'react';
import { FiBookOpen, FiCheckCircle, FiClock, FiShield, FiZap, FiUsers, FiAward, FiBarChart2 } from 'react-icons/fi';

const NEPCompliance = () => {
  const nepRequirements = [
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Holistic and Multidisciplinary Education',
      description: 'NEP 2020 emphasizes a holistic, multidisciplinary education approach. Institutions must offer flexible curriculum choices, allow students to explore multiple disciplines, and move away from rigid stream-based education.',
      stat: '75% of institutions are redesigning curricula for NEP 2020',
      solution: 'Truyon supports flexible curriculum design with multidisciplinary program structures and choice-based credit systems.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student-Centric Learning',
      description: 'The policy prioritizes student-centric learning with personalized pathways, experiential learning, and flexible progression. Students should be able to learn at their own pace and choose their learning paths.',
      stat: '85% of institutions are adopting student-centric approaches',
      solution: 'Truyon enables personalized learning paths, flexible progression, and competency-based tracking.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Assessment and Evaluation Reform',
      description: 'NEP 2020 calls for competency-based assessments that test understanding rather than rote memorization. Multiple assessment types and continuous evaluation are emphasized.',
      stat: '70% of institutions are reforming assessment methods',
      solution: 'Truyon supports multiple assessment types, competency-based grading, and continuous evaluation tracking.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Academic Bank of Credits (ABC)',
      description: 'NEP introduces the Academic Bank of Credits, allowing students to earn and store credits digitally. Credits can be transferred across institutions, enabling academic mobility and flexibility.',
      stat: '95% of institutions are implementing ABC integration',
      solution: 'Truyon integrates with the Academic Bank of Credits for seamless credit management and transfer.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Technology Integration in Education',
      description: 'NEP 2020 emphasizes technology integration in teaching, learning, and administration. Digital infrastructure, online learning platforms, and data-driven decision-making are key priorities.',
      stat: '90% of institutions are investing in technology infrastructure',
      solution: 'Truyon provides a unified digital platform for administration, learning, and analytics.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Teacher Training and Professional Development',
      description: 'The policy emphasizes continuous professional development for teachers. Institutions must invest in teacher training, skill development, and regular performance assessment.',
      stat: '80% of institutions are revamping teacher training programs',
      solution: 'Truyon tracks teacher training, certifications, and professional development progress.',
      color: 'bg-blue-50'
    }
  ];

  const complianceBenefits = [
    {
      title: 'Academic Bank of Credits Integration',
      description: 'Seamless credit management and transfer across institutions.',
      icon: <FiBookOpen className="w-5 h-5" />
    },
    {
      title: 'Flexible Curriculum Management',
      description: 'Support multidisciplinary programs and choice-based credit systems.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Competency-Based Assessment',
      description: 'Track learning outcomes and competency development.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Digital Record Keeping',
      description: 'Maintain comprehensive digital records for all students and programs.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Teacher Development Tracking',
      description: 'Monitor teacher training, certifications, and performance.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Data-Driven Compliance Reporting',
      description: 'Generate compliance reports for regulatory bodies automatically.',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The National Education Policy (NEP) 2020 is the most significant education policy reform in India's history. It reimagines education—from curriculum design to assessment methods, from teacher training to technology integration. For institutions, NEP 2020 represents both opportunity and challenge.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The opportunity is clear: NEP 2020 empowers institutions to innovate, design flexible curricula, and focus on student outcomes. <strong className="text-slate-900">The challenge is implementing these changes without the right systems.</strong> Institutions that try to comply using manual processes or outdated ERPs will struggle.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how a modern educational ERP helps institutions meet NEP 2020 compliance standards.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">85%</div>
          <div className="text-sm text-slate-500">Institutions adopting student-centric approaches</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">95%</div>
          <div className="text-sm text-slate-500">Implementing Academic Bank of Credits</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">90%</div>
          <div className="text-sm text-slate-500">Investing in technology integration</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">NEP 2020 Requirements and How ERP Helps</h2>

      {/* Requirements List */}
      {nepRequirements.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Compliance Benefits */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Truyon Delivers for NEP 2020 Compliance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {complianceBenefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">NEP 2020 Implementation Timeline</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">2023-2025</h4>
            <p className="text-xs text-slate-600">Foundation building and pilot programs</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">2025-2027</h4>
            <p className="text-xs text-slate-600">Full implementation and scaling</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-sm">2027-2030</h4>
            <p className="text-xs text-slate-600">Maturity and continuous improvement</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 text-center mt-3">The time to act is now. Institutions must implement systems that support NEP 2020 compliance.</p>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Manual vs. ERP-Supported NEP Compliance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Manual Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Spreadsheets for everything</span></li>
              <li className="flex items-start gap-2">• <span>Manual credit tracking</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent assessment records</span></li>
              <li className="flex items-start gap-2">• <span>Difficult compliance reporting</span></li>
              <li className="flex items-start gap-2">• <span>High risk of errors</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">ERP-Supported Compliance</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Automated credit management</span></li>
              <li className="flex items-start gap-2">• <span>ABC integration ready</span></li>
              <li className="flex items-start gap-2">• <span>Competency-based assessment</span></li>
              <li className="flex items-start gap-2">• <span>Automated compliance reports</span></li>
              <li className="flex items-start gap-2">• <span>Accurate, auditable records</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for NEP 2020 Compliance</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Flexible Curriculum</h4>
            <p className="text-xs text-slate-600">Support multidisciplinary programs</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">ABC Integration</h4>
            <p className="text-xs text-slate-600">Academic Bank of Credits ready</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Competency-Based Assessment</h4>
            <p className="text-xs text-slate-600">Track learning outcomes</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Digital Records</h4>
            <p className="text-xs text-slate-600">Comprehensive student records</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Compliance Reporting</h4>
            <p className="text-xs text-slate-600">Automated regulatory reports</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Data Security</h4>
            <p className="text-xs text-slate-600">Secure and compliant record keeping</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Stay NEP 2020 Compliant with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          NEP 2020 is transforming Indian education. Institutions that embrace its vision and implement the right systems will thrive in the new educational landscape. Those that lag behind will struggle to comply and compete.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is built to support NEP 2020 compliance. Flexible curriculum management, Academic Bank of Credits integration, competency-based assessment, digital records, and automated compliance reporting—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for NEP 2020 compliance.</strong> Be ready for the future of education.
        </p>
      </div>
    </>
  );
};

export default NEPCompliance;